import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../api";
import type { CampaignPace, PaceLine, PortfolioPace } from "../api";
import { dayLabel, money, signedMoney, signedPct } from "./format";

/**
 * The month so far, against the same days of last month: who is behind,
 * which campaigns stopped, who is heading below their commitment.
 *
 * Computed on request from the mirror, through yesterday -- no model and
 * nothing stored -- so it is never more than an hour stale. Like for like
 * rather than a projection: a straight line through a customer that dials
 * in bursts says whatever the day it is asked on says.
 */
export default function PacePanel({ user }: { user: string | null }) {
  const [data, setData] = useState<PortfolioPace | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    const q = new URLSearchParams();
    if (user) q.set("user", user);
    void get<PortfolioPace>(`/api/revenue/pace?${q}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return null;
  if (!data.as_of) {
    return (
      <section className="rev-brief">
        <strong>This month so far</strong>
        <p className="dim">Pace starts tomorrow, once a whole day of the month is in.</p>
      </section>
    );
  }

  return (
    <section className="rev-brief">
      <div className="rev-brief-head">
        <strong>This month so far</strong>
        <span className="dim">
          1–{dayLabel(data.as_of)} against the same {data.days_compared} days of last month
        </span>
      </div>
      {data.too_early && (
        <p className="dim">Too early in the month to call anything; figures are shown, nothing is flagged.</p>
      )}
      {data.groups.map((g) => (
        <div key={g.currency} className="rev-movers">
          <div className="rev-pace-total">
            {data.groups.length > 1 && <span className="dim">{g.currency} · </span>}
            {money(g.mtd, g.currency)} so far against {money(g.prior_mtd, g.currency)} at this point last
            month{" "}
            <span className={Number(g.mtd) >= Number(g.prior_mtd) ? "pos" : "neg"}>
              ({signedMoney(Number(g.mtd) - Number(g.prior_mtd), g.currency)})
            </span>
          </div>

          <div className="rev-movers-cols">
            <Lines title="Behind" lines={g.behind} total={g.behind_count} currency={g.currency} />
            <Lines title="Ahead" lines={g.ahead} total={g.ahead_count} currency={g.currency} />
          </div>

          {g.stopped.length > 0 && (
            <div>
              <div className="rev-movers-t">Stopped billing</div>
              <ul className="rev-pace-list">
                {g.stopped.map((s) => (
                  <li key={`${s.slug}-${s.campaign.campaign_id}`}>
                    <Link to={`/c/${s.slug}/revenue`}>{s.name}</Link> · {s.campaign.name}{" "}
                    <span className="dim">{stoppedSince(s.campaign)}</span>
                    <span className="dim">
                      {" "}
                      · billed {money(s.campaign.prior_month, g.currency)} last month
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {g.at_risk.length > 0 && (
            <div>
              <div className="rev-movers-t">At risk this month</div>
              <ul className="rev-pace-list">
                {g.at_risk.map((r) => (
                  <li key={r.slug}>
                    <Link to={`/c/${r.slug}/revenue`}>{r.name}</Link>{" "}
                    {r.mmc_short_minutes !== null && (
                      <span className="neg">
                        heading {Math.round(Number(r.mmc_short_minutes)).toLocaleString("en-IN")} min below
                        its commitment
                      </span>
                    )}
                    {r.mmc_short_minutes !== null && r.tier_change && " · "}
                    {r.tier_change && (
                      <span className="neg">
                        on pace for {r.tier_change.to} (last month {r.tier_change.from})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {g.on_pace > 0 && <div className="dim">{g.on_pace} on pace with last month.</div>}
        </div>
      ))}
    </section>
  );
}

export function stoppedSince(c: CampaignPace): string {
  return c.last_billed ? `nothing since ${dayLabel(c.last_billed)}` : "nothing this month";
}

function Lines({
  title,
  lines,
  total,
  currency,
}: {
  title: string;
  lines: PaceLine[];
  total: number;
  currency: string;
}) {
  if (lines.length === 0) return null;
  return (
    <div>
      <div className="rev-movers-t">
        {title}
        {total > lines.length && <span className="dim"> · top {lines.length} of {total}</span>}
      </div>
      <ul>
        {lines.map((l) => (
          <li key={l.slug}>
            <Link to={`/c/${l.slug}/revenue`}>{l.name}</Link>{" "}
            <span className={Number(l.delta) < 0 ? "neg" : "pos"}>{signedMoney(l.delta, currency)}</span>{" "}
            <span className="dim">({signedPct(l.delta_pct)})</span>
            {l.lead && (
              <div className="dim rev-mover-h">
                {l.lead.state === "stopped"
                  ? `${l.lead.name} stopped — ${stoppedSince(l.lead)}`
                  : l.lead.state === "new"
                    ? `${l.lead.name} is new this month, ${money(l.lead.mtd, currency)}`
                    : `${l.lead.name} ${signedMoney(l.lead.delta, currency)} (${signedPct(l.lead.delta_pct)})`}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
